import type { AllowedBy, Block, GrantSource, PanelLine, Permission } from "../../../api/access/accessModel";
import { permissionRank } from "../model/permissionCatalogue";

/**
 * The access panel's rows (redesign R3-2 "The access panel"): one per permission, ✓ or –.
 * The server answers Curate once per item kind; the panel shows it as one row naming the
 * kinds it covers and how many it doesn't ("not the other 12 kinds"), as the design does.
 */
export interface PanelRow {
  readonly permission: Permission;
  readonly allowed: boolean;
  /** For Curate: the kinds allowed, in the server's order; empty for every other row. */
  readonly kinds: readonly string[];
  /** For Curate: how many kinds it doesn't cover. */
  readonly otherKinds: number;
  readonly sources: readonly GrantSource[];
  readonly allowedBy: AllowedBy | null;
  /** For a – row: the groups here that give it. */
  readonly withIt: readonly string[];
  /** For a – row stopped whatever anyone holds. */
  readonly block: Block | null;
}

export function panelRows(lines: readonly PanelLine[]): readonly PanelRow[] {
  const rows: PanelRow[] = [];
  const curate = lines.filter((line) => line.grant.permission === "curate");
  for (const line of lines) {
    if (line.grant.permission === "curate") continue;
    rows.push({
      permission: line.grant.permission,
      allowed: line.allowed,
      kinds: [],
      otherKinds: 0,
      sources: line.sources,
      allowedBy: line.allowedBy,
      withIt: line.withIt,
      block: line.refusal?.block ?? null,
    });
  }
  if (curate.length > 0) rows.push(curateRow(curate));
  return rows.sort((left, right) => permissionRank(left.permission) - permissionRank(right.permission));
}

function curateRow(lines: readonly PanelLine[]): PanelRow {
  const allowed = lines.filter((line) => line.allowed);
  const counted = allowed.length > 0 ? allowed : lines;
  return {
    permission: "curate",
    allowed: allowed.length > 0,
    kinds: allowed.flatMap((line) => (line.grant.itemKind === null ? [] : [line.grant.itemKind])),
    otherKinds: allowed.length > 0 ? lines.length - allowed.length : 0,
    sources: uniqueSources(allowed.flatMap((line) => line.sources)),
    allowedBy: allowed.find((line) => line.allowedBy !== null)?.allowedBy ?? null,
    withIt: allowed.length > 0 ? [] : [...new Set(counted.flatMap((line) => line.withIt))],
    block: allowed.length > 0 ? null : (lines.map((line) => line.refusal?.block ?? null).find((block) => block !== null) ?? null),
  };
}

function uniqueSources(sources: readonly GrantSource[]): readonly GrantSource[] {
  const seen = new Map<string, GrantSource>();
  for (const source of sources) {
    const key = `${source.path.join("/")}|${String(source.here)}`;
    if (!seen.has(key)) seen.set(key, source);
  }
  return [...seen.values()];
}
